import React, { useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useGetUserBundlesQuery } from '@features/subscriptions';
import { useIsAuthenticated, useUserState } from '@features/user';
import { useStatsigUserSync } from '@jotpsych/statsig';

import { CenteredSpinner } from '../../../components/ui/feedback/CenteredSpinner';
import { ANONYMOUS_STATSIG_USER } from '../anonymousUser';
import { buildStatsigUser } from '../utils/buildStatsigUser';

/**
 * Gates rendering until Statsig is initialized with the authenticated user.
 *
 * Wraps content that uses feature gates and needs accurate user targeting.
 * Delegates the sync mechanism (stable-hash memo, ready gate, fail-open) to
 * `useStatsigUserSync` from the shared package — this component owns only
 * the JotPsych-specific user-object builder and loading fallback.
 */
export const StatsigUserGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useIsAuthenticated();
  const { user: auth0User } = useAuth0();
  const userState = useUserState({
    skip: !isAuthenticated,
  });
  const { data: userBundlesData } = useGetUserBundlesQuery(undefined, {
    skip: !isAuthenticated,
  });

  // `useUserState` spreads the raw user-info response (snake_case) under its
  // camelCase aliases; `buildStatsigUser` reads the raw fields and its parameter
  // type pins that shape, so dropping the spread fails typecheck rather than
  // silently emptying Statsig targeting.
  const statsigUser = useMemo(
    () => buildStatsigUser(userState, userBundlesData, auth0User?.email),
    [userState, userBundlesData, auth0User?.email],
  );

  const { isReady } = useStatsigUserSync({
    user: statsigUser,
    isAuthenticated,
    anonymousUser: ANONYMOUS_STATSIG_USER,
  });

  if (!isReady) {
    return <CenteredSpinner sx={{ height: '100dvh' }} />;
  }

  return <>{children}</>;
};
